import {
  Children,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Flex,
  Box,
  Text,
  useMultiStyleConfig,
  BoxProps,
  FlexProps,
  TextProps,
  Icon,
  Divider,
  HStack,
} from '@chakra-ui/react';
import {
  AiOutlineCheck,
  AiOutlineClose,
  AiOutlineRight,
} from 'react-icons/ai/index.js';

export interface StepsContextProps {
  currentStep: number;
  setCurrentstep: (val: number) => void;
  colorScheme?: string;
}

export type StepStatus = 'error' | 'wait' | 'process' | 'finish';

export type StepsProps = {
  isVertical?: boolean;
  children?: React.ReactNode;
  current?: number;
  colorScheme?: string;
  stepConnector?: 'line' | 'arrow';
} & FlexProps;

export type StepProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  status: StepStatus;
  onStepClick?: () => void;
  iconProps?: BoxProps;
  detailsProps?: FlexProps;
  titleProps?: TextProps;
  descriptionProps?: TextProps;
  showBottomBorder?: boolean;
} & Omit<FlexProps, 'title'>;

const StepContext = createContext<number>(0);
const StepsContext = createContext<StepsContextProps>({
  currentStep: 0,
  setCurrentstep: () => undefined,
  colorScheme: 'raspberry',
});
const StepContextProvider = StepContext.Provider;
const StepsContextProvider = StepsContext.Provider;
const useStepContextContext = () => useContext(StepContext);
const useStepsContextContext = () => useContext(StepsContext);

export const Steps = (props: StepsProps): JSX.Element => {
  const {
    isVertical = false,
    className,
    style,
    children,
    current = 0,
    stepConnector = 'arrow',
    'aria-label': ariaLabel = 'progress',
    ...rest
  } = props;

  const styles = useMultiStyleConfig(`Steps`, {});
  const [currentStep, setCurrentstep] = useState<number>(0);
  let { colorScheme } = useStepsContextContext();
  colorScheme = props.colorScheme;
  useEffect(() => {
    setCurrentstep(current);
  }, [current]);
  const canApplySpaceBetweenToSteps = Children.count(children) >= 5;

  return (
    <StepsContextProvider value={{ currentStep, setCurrentstep, colorScheme }}>
      <Flex
        className={className}
        style={style}
        aria-label={ariaLabel}
        flexDirection={isVertical ? 'column' : 'row'}
        sx={{
          ...styles.StepsContainer,
          ...(canApplySpaceBetweenToSteps
            ? {}
            : { justifyContent: 'unset', gap: 4 }),
          ...rest,
        }}
      >
        {Children.map(children, (child, index) => {
          return (
            <>
              <StepContextProvider value={index}>{child}</StepContextProvider>
              {index !== Children.count(children) - 1 && (
                <Flex
                  {...(isVertical
                    ? { height: '48px', width: '0.5px' }
                    : {
                        width: canApplySpaceBetweenToSteps ? '100%' : 'unset',
                        height: '0.5px',
                      })}
                  sx={{ ...styles.StepConnector }}
                >
                  {stepConnector === 'arrow' ? (
                    <Icon as={AiOutlineRight} />
                  ) : (
                    <Divider sx={{ ...styles.StepConnectorElement }} />
                  )}
                </Flex>
              )}
            </>
          );
        })}
      </Flex>
    </StepsContextProvider>
  );
};

const Step = (props: StepProps): JSX.Element => {
  const {
    currentStep,
    setCurrentstep: setCurrentStep,
    colorScheme,
  } = useStepsContextContext();
  const index = useStepContextContext();

  const {
    title,
    description,
    style,
    className,
    status,
    onStepClick: _onClick,
    'aria-current': ariaCurrent = index === currentStep,
    iconProps,
    detailsProps,
    titleProps,
    descriptionProps,
    showBottomBorder,
    ...rest
  } = props;

  const styles = useMultiStyleConfig(`Steps`, { status, colorScheme });

  const handleClick = useCallback(() => {
    const newIndex = index <= currentStep ? currentStep : 0;
    setCurrentStep(newIndex);
    if (_onClick) {
      _onClick();
    }
  }, [_onClick, currentStep, index, setCurrentStep]);

  return (
    <Flex
      className={className}
      style={style}
      onClick={handleClick}
      aria-current={ariaCurrent}
      sx={{ ...styles.Step, ...rest }}
    >
      <Box
        borderBottom={showBottomBorder && status === 'process' ? '2px' : '0px'}
        borderColor={'blue.800'}
      >
        <HStack>
          <Box
            sx={{
              ...styles.StepIcon,
              ...iconProps,
            }}
          >
            {status && (status === 'wait' || status === 'process') ? (
              index + 1
            ) : status === 'error' ? (
              <AiOutlineClose />
            ) : (
              <AiOutlineCheck />
            )}
          </Box>
          <Flex
            sx={{ ...styles.StepDetails, ...detailsProps }}
            direction='column'
          >
            <Text sx={{ ...styles.StepTitle, ...titleProps }}>{title}</Text>
            <Text sx={{ ...styles.StepDescription, ...descriptionProps }}>
              {description}
            </Text>
          </Flex>
        </HStack>
      </Box>
    </Flex>
  );
};

Steps.Step = Step;
